import type { ReactElement } from 'react'
import { IconLocationFill } from './icons/IconLocationFill'
import styles from './MyLocationButton.module.css'

export type MyLocationButtonVariant = 'phone' | 'tablet'

export interface MyLocationButtonProps {
  /** Called when the user taps the my-location control. */
  readonly onClick: () => void
  /**
   * Layout variant — drives button size (44×44 phone per Figma spec §1.3
   * / 52×52 tablet, matching the {@link SwapButton} tablet sizing).
   *
   * @defaultValue `'tablet'`
   */
  readonly variant?: MyLocationButtonVariant
  /**
   * `true` while a location fix is being resolved — swaps the glyph for a
   * spinner (in `text/secondary`, the same colour as the location glyph it
   * replaces) and disables the button so the user can't re-fire the
   * request mid-flight (TASK-391 §3.3, cross-platform 2026-06-18). Mirrors
   * the iOS `MyLocationButtonView.isLoading`.
   *
   * @defaultValue `false`
   */
  readonly isLoading?: boolean
  /** Accessible label. @defaultValue `'My location'` */
  readonly ariaLabel?: string
}

/**
 * Round button that triggers the TASK-391 "my location" geolocation flow
 * — snap the map (and, when appropriate, the departure station) to the
 * user's nearest station.
 *
 * Per the RouteCard Figma spec (2026-05-22 §1.3, §2.1), the collapsed
 * phone layout renders this on a **separate top row ABOVE** the From/To
 * pills, trailing-aligned — see {@link DestinationButtons}.
 *
 * Icon ({@link IconLocationFill}) is the byte-identical
 * `icon-location-fill.svg` (18×18 filled paper-plane), tinted via
 * `currentColor` → `--mm-color-text-secondary`.
 */
export function MyLocationButton({
  onClick,
  variant = 'tablet',
  isLoading = false,
  ariaLabel = 'My location',
}: MyLocationButtonProps): ReactElement {
  return (
    <button
      type="button"
      aria-label={ariaLabel}
      className={`${styles.button} ${styles[variant]}`}
      data-testid="route-card-my-location"
      data-variant={variant}
      data-loading={isLoading || undefined}
      // Block re-firing the request while a fix is resolving — the host
      // clears `isLoading` on resolve / fail / timeout (TASK-391 §3.3).
      disabled={isLoading}
      onClick={onClick}
    >
      {isLoading ? (
        <span className={styles.spinner} data-testid="route-card-my-location-spinner" />
      ) : (
        <IconLocationFill className={styles.icon} width="18" height="18" />
      )}
    </button>
  )
}
